// ICE COLD — "induction" buttons: electric arcs crawling around a dark plate, drawn in WebGL.
// Adapted from the "Induction Button" effect. Markup:
//   <a class="ice-induction" data-tone="bordeaux|ice" [data-shape="round"]><canvas class="ice-induction__canvas"></canvas><span class="ice-induction__label">…</span></a>

(() => {
  if (window.IceInduction) return;

  const FS = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_arcs;
uniform float u_flash;
uniform vec3 u_arc;
uniform vec3 u_core;
uniform vec3 u_glow;
uniform vec3 u_plate;
uniform float u_round;
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123);}
float noise(vec2 p){
  vec2 i=floor(p), f=fract(p);
  vec2 u=f*f*(3.0-2.0*f);
  return mix(mix(hash(i),hash(i+vec2(1.,0.)),u.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),u.x),u.y);
}
float fbm(vec2 p){
  float v=0.0; float a=0.5;
  for(int i=0;i<4;i++){ v+=a*noise(p); p=p*2.05+vec2(9.7,3.1); a*=0.5; }
  return v;
}
float sdRBox(vec2 p, vec2 b, float r){
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}
void main(){
  vec2 p = (gl_FragCoord.xy - 0.5 * u_res) / u_res.y;
  float ar = u_res.x / u_res.y;
  float d = sdRBox(p, vec2(ar * 0.5 - 0.2, 0.3), mix(0.14, 0.3, u_round));
  float hover = clamp(u_arcs / 6.0, 0.0, 1.0);
  float plate = 1.0 - smoothstep(-0.004, 0.004, d);
  vec3 plateCol = u_plate + u_plate * 0.55 * fbm(p * 9.0);
  plateCol += u_glow * exp(d * 9.0) * (0.25 + hover * 0.6);
  float a = atan(p.y, p.x);
  vec3 arcCol = vec3(0.0);
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float w = clamp(u_arcs - fi, 0.0, 1.0);
    float n1 = fbm(vec2(a * 2.4 + fi * 11.3, u_time * (1.6 + fi * 0.27) + fi * 53.1));
    float off = (n1 - 0.5) * (0.11 + u_flash * 0.1);
    float seg = 0.3 + 0.7 * smoothstep(0.35, 0.75, noise(vec2(a * 1.8 + fi * 7.7, u_time * (0.9 + fi * 0.13) + fi * 19.0)));
    float g = 0.0042 / (abs(d + off) + 0.006);
    arcCol += (u_arc * g + u_core * g * g * 0.55) * w * seg;
  }
  float outerMask = 1.0 - smoothstep(0.04, 0.15, d);
  vec3 glow = arcCol * (0.6 + 0.4 * hover) * outerMask;
  float ring = 0.006 / (abs(d) + 0.006);
  glow += u_core * ring * u_flash * 1.5 * outerMask;
  glow += u_core * u_flash * 0.16 * outerMask;
  // Transparent outside the plate so whatever is behind shows through the glow.
  vec3 col = plateCol * plate + glow;
  float alpha = clamp(max(plate, max(col.r, max(col.g, col.b))), 0.0, 1.0);
  gl_FragColor = vec4(min(col, vec3(alpha)), alpha);
}`;

  const TONES = {
    bordeaux: { arc: [0.85, 0.12, 0.32], core: [1.0, 0.78, 0.85], glow: [0.32, 0.03, 0.1], plate: [0.055, 0.018, 0.03] },
    ice: { arc: [0.55, 0.75, 0.95], core: [0.92, 0.97, 1.0], glow: [0.12, 0.2, 0.3], plate: [0.03, 0.04, 0.05] },
  };

  class IceInduction {
    constructor(link) {
      this.link = link;
      link.dataset.inductionReady = '';
      this.canvas = link.querySelector('.ice-induction__canvas');
      const gl = this.canvas && this.canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: true });
      if (!gl) {
        link.classList.add('is-fallback');
        return;
      }
      this.gl = gl;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.arcs = 2.4;
      this.arcsTarget = 2.4;
      this.flash = 0;
      this.crawl = 0;
      this.last = performance.now();
      this.onScreen = true;

      const shader = (type, src) => {
        const sh = gl.createShader(type);
        gl.shaderSource(sh, src);
        gl.compileShader(sh);
        return sh;
      };
      const prog = gl.createProgram();
      gl.attachShader(prog, shader(gl.VERTEX_SHADER, 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}'));
      gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        link.classList.add('is-fallback');
        this.gl = null;
        return;
      }
      gl.useProgram(prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'p');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      const u = (name) => gl.getUniformLocation(prog, name);
      this.u = { res: u('u_res'), time: u('u_time'), arcs: u('u_arcs'), flash: u('u_flash') };
      const tone = TONES[link.dataset.tone] || TONES.bordeaux;
      gl.uniform3fv(u('u_arc'), tone.arc);
      gl.uniform3fv(u('u_core'), tone.core);
      gl.uniform3fv(u('u_glow'), tone.glow);
      gl.uniform3fv(u('u_plate'), tone.plate);
      // data-shape="round": a disc instead of a rounded plate (square canvas).
      gl.uniform1f(u('u_round'), link.dataset.shape === 'round' ? 1 : 0);

      const charge = () => (this.arcsTarget = 5.8);
      const calm = () => (this.arcsTarget = 2.4);
      link.addEventListener('pointerenter', charge);
      link.addEventListener('pointerleave', calm);
      link.addEventListener('focus', charge);
      link.addEventListener('blur', calm);
      link.addEventListener('pointerdown', () => (this.flash = 1));

      // Only draw while the button is on screen (and, in the landing film, while its moment is shown).
      new IntersectionObserver((entries) => (this.onScreen = entries[0].isIntersecting)).observe(link);
      this.gate = link.closest('[data-start]');

      this.frame = this.frame.bind(this);
      requestAnimationFrame(this.frame);
    }

    frame(now) {
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      if (this.onScreen && (!this.gate || this.gate.classList.contains('is-visible'))) {
        const gl = this.gl;
        this.arcs += (this.arcsTarget - this.arcs) * Math.min(1, dt * 5);
        this.flash *= Math.exp(-3.6 * dt);
        this.crawl += dt * (0.6 + (this.arcs / 6) * 1.1 + this.flash * 2);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
        const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
        if (this.canvas.width !== w || this.canvas.height !== h) {
          this.canvas.width = w;
          this.canvas.height = h;
          gl.viewport(0, 0, w, h);
        }
        gl.uniform2f(this.u.res, w, h);
        gl.uniform1f(this.u.time, this.reduced ? 3 : this.crawl);
        gl.uniform1f(this.u.arcs, this.arcs);
        gl.uniform1f(this.u.flash, this.flash);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
      requestAnimationFrame(this.frame);
    }
  }

  const init = (root = document) =>
    root.querySelectorAll('.ice-induction:not([data-induction-ready])').forEach((link) => new IceInduction(link));

  window.IceInduction = IceInduction;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
